import type { HelpRole } from '@hoopoe/shared';
import MiniSearch, { type SearchResult } from 'minisearch';
import { type Article, articlesFor, GLOSSARY, GLOSSARY_SLUG } from './articles';

export interface HelpHit {
  id: string;
  slug: string;
  /** Heading anchor to jump to. */
  section?: string;
  title: string;
  heading?: string;
  snippet: string;
  score: number;
}

interface Doc {
  id: string;
  slug: string;
  section?: string;
  title: string;
  heading: string;
  text: string;
  tags: string;
}

const plain = (md: string) =>
  md
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/!\[[^\]]*]\([^)]*\)/g, ' ')
    .replace(/\[([^\]]+)]\([^)]*\)/g, '$1')
    .replace(/[#*_`>|]/g, ' ')
    .replace(/-{3,}/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/** One search document per article section, so results jump to the right heading. */
function sections(a: Article): Doc[] {
  const parts = a.body.split(/^(?=#{2,3}\s)/m);
  return parts.map((part, i) => {
    const h = part.match(/^#{2,3}\s+(.+)$/m);
    const heading = i === 0 && !h ? '' : (h?.[1] ?? '').replace(/[*_`]/g, '');
    const section = heading ? a.headings.find(x => x.text === heading)?.id : undefined;
    return {
      id: `${a.slug}#${i}`,
      slug: a.slug,
      section,
      title: a.title,
      heading,
      text: plain(part.replace(/^#{2,3}\s+.+$/m, '')),
      tags: (a.tags ?? []).join(' '),
    };
  });
}

/** Typo-tolerant: one edit for words of 4–7 letters, two for longer words (PLAN §12.6). */
const fuzzy = (term: string) => (term.length <= 3 ? 0 : term.length <= 7 ? 1 : 2);

export function buildHelpIndex(role: HelpRole, extra: Article[] = []) {
  const ms = new MiniSearch<Doc>({
    fields: ['title', 'heading', 'text', 'tags'],
    storeFields: ['slug', 'section', 'title', 'heading', 'text'],
    searchOptions: { fuzzy, prefix: t => t.length >= 3, boost: { title: 3, heading: 2, tags: 2 }, combineWith: 'AND' },
  });
  ms.addAll(articlesFor(role, extra).flatMap(sections));
  ms.addAll(
    GLOSSARY.map(g => ({
      id: `glossary:${g.id}`,
      slug: GLOSSARY_SLUG,
      section: g.id,
      title: 'Glossary',
      heading: g.term,
      text: g.definition,
      tags: 'glossary definition',
    })),
  );
  return {
    search(query: string, limit = 12): HelpHit[] {
      const q = query.trim();
      if (!q) return [];
      let results = ms.search(q);
      if (!results.length) results = ms.search(q, { combineWith: 'OR' });
      return results.slice(0, limit).map(r => toHit(r, q));
    },
  };
}

function toHit(r: SearchResult, query: string): HelpHit {
  const text = String(r.text ?? '');
  const words = [...Object.keys(r.match), ...query.toLowerCase().split(/\s+/)];
  const lower = text.toLowerCase();
  const at =
    words
      .map(w => lower.indexOf(w.toLowerCase()))
      .filter(i => i >= 0)
      .sort((a, b) => a - b)[0] ?? 0;
  const start = Math.max(0, at - 60);
  const snippet = `${start > 0 ? '…' : ''}${text.slice(start, start + 160).trim()}${start + 160 < text.length ? '…' : ''}`;
  return {
    id: String(r.id),
    slug: String(r.slug),
    section: r.section ? String(r.section) : undefined,
    title: String(r.title),
    heading: r.heading ? String(r.heading) : undefined,
    snippet,
    score: r.score,
  };
}
